import { useState, useEffect, useRef } from 'react';

interface RestaurantImageProps {
  imageUrl: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}

/**
 * RestaurantImage component that handles authenticated image loading
 * Fetches images with authentication headers and converts to blob URL
 */
export function RestaurantImage({ 
  imageUrl, 
  alt, 
  className = '', 
  fallbackSrc = 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&auto=format&fit=crop'
}: RestaurantImageProps) {
  const [src, setSrc] = useState<string>(fallbackSrc);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // Cleanup previous blob URL if it exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // 空值防护：后端返回的餐厅数据中某些餐厅可能没有图片 URL，
    // 此时 imageUrl 为 undefined/null/空字符串，直接使用 fallback 图片
    if (!imageUrl) {
      setSrc(fallbackSrc);
      setIsLoading(false);
      return;
    }

    // Check if imageUrl is a backend proxy URL that needs authentication
    // 1.3.50: 支持本地环境的maps-proxy URL（127.0.0.1 或 localhost）
    const isBackendUrl = imageUrl.includes('supabase.co/functions/v1/maps-proxy') ||
                         imageUrl.includes('/functions/v1/maps-proxy'); // 本地环境
    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

    // If it's a backend URL and we have anon key, fetch with authentication
    if (isBackendUrl && SUPABASE_ANON_KEY) {
      setIsLoading(true);
      setHasError(false);

      fetch(imageUrl, {
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            if (import.meta.env.DEV) {
              console.error('Failed to fetch image:', response.status, response.statusText, imageUrl);
            }
            throw new Error(`Failed to fetch image: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const newBlobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = newBlobUrl;
          setSrc(newBlobUrl);
          setIsLoading(false);
        })
        .catch((error) => {
          // Error loading image (error logged only in dev via logger)
          setHasError(true);
          setSrc(fallbackSrc);
          setIsLoading(false);
        });
    } else if (isBackendUrl && !SUPABASE_ANON_KEY) {
      // Backend URL but no anon key - log warning and use fallback
      // SUPABASE_ANON_KEY not configured (warning logged only in dev)
      setHasError(true);
      setSrc(fallbackSrc);
      setIsLoading(false);
    } else {
      // For non-backend URLs, use the URL directly
      setSrc(imageUrl);
      setIsLoading(false);
    }

    // Cleanup blob URL on unmount or when imageUrl changes
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imageUrl, fallbackSrc]);

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        if (!hasError) {
          setHasError(true);
          setSrc(fallbackSrc);
        }
      }}
      style={{
        opacity: isLoading ? 0.5 : 1,
        transition: 'opacity 0.3s ease-in-out',
      }}
    />
  );
}

